import { ScreenReaderElement } from '@components/screen-reader/screen-reader-element';
import { formatNumber, formatNumberWithCurrency } from '@eika-infrastruktur/format';
import { useInputs } from '@hooks/use-inputs';
import { useResultsExpectedRate } from '@hooks/use-results-expected-rate';
import { useSavingsAccountComparison } from '@hooks/use-savings-account-comparison';
import { format } from '@translation-utils/use-translation';
import { useTranslation } from '@translations';
import { EikaChartTheme } from '../data-chart-panel/data-chart/theme';
import { FundChartLabel, SavingsAccountChartLabel, TotalInvestedLabel } from './category-label/category-label';
import { ProfitTag } from './profit-tag/profit-tag';

export const InvestmentTotals = () => {
    const expectedResults = useResultsExpectedRate();

    if (!expectedResults) {
        return null;
    }

    return (
        <div className="totals-panel__category totals-panel__category--investment">
            <TotalInvestedLabel color={EikaChartTheme.eikaPalette.typography} />
            <p className="totals-panel__category__total-invested">
                {formatNumberWithCurrency(expectedResults.totalInvested, 'kr', 0)}
            </p>
        </div>
    );
};

export const FundTotals = () => {
    const expectedResults = useResultsExpectedRate();
    const { totalsPanel } = useTranslation();

    if (!expectedResults) {
        return null;
    }

    const { totalGained, totalBalance, interestRate } = expectedResults;

    return (
        <CategoryTotals
            chartLabel={<FundChartLabel color={EikaChartTheme.eikaPalette.chartInvested} />}
            totalBalance={totalBalance}
            totalGained={totalGained}
            interestRate={interestRate}
            lightColor={EikaChartTheme.eikaPalette.chartInvestedLight}
            modifier="fund"
            labels={{
                expectedTotalValueLabel: totalsPanel.expectedTotalValueLabel,
                percentageLabel: totalsPanel.increasePercentageLabel,
                amountLabel: totalsPanel.increaseAmountLabel,
            }}
        />
    );
};

export const SavingsAccountTotals = () => {
    const savingsAccountComparison = useSavingsAccountComparison();
    const { compareWithSavingsAccount } = useInputs();
    const { totalsPanel } = useTranslation();

    if (!savingsAccountComparison || !compareWithSavingsAccount) {
        return null;
    }

    return (
        <CategoryTotals
            chartLabel={<SavingsAccountChartLabel color={EikaChartTheme.eikaPalette.chartSavingsAccount} />}
            totalBalance={savingsAccountComparison.totalBalance}
            totalGained={savingsAccountComparison.totalGained}
            interestRate={savingsAccountComparison.interestRate}
            lightColor={EikaChartTheme.eikaPalette.chartSavingsAccountLight}
            modifier="savings-account"
            labels={{
                expectedTotalValueLabel: totalsPanel.expectedTotalValueLabel,
                percentageLabel: totalsPanel.interestPercentageLabel,
                amountLabel: totalsPanel.interestAmountLabel,
            }}
        />
    );
};

type CategoryTotalsLabels = {
    expectedTotalValueLabel: string;
    percentageLabel: string;
    amountLabel: string;
};

type CategoryTotalsProps = {
    chartLabel: React.ReactNode;
    totalBalance: number;
    totalGained: number;
    interestRate: number;
    lightColor: string;
    modifier: 'fund' | 'savings-account';
    labels: CategoryTotalsLabels;
};

const CategoryTotals = ({
    chartLabel,
    totalBalance,
    totalGained,
    interestRate,
    lightColor,
    modifier,
    labels,
}: CategoryTotalsProps) => (
    <div className={`totals-panel__category totals-panel__category--${modifier}`}>
        <div className="totals-panel__category-desktop">
            {chartLabel}
            <TotalGained gained={totalGained} />
            <TotalWithInterestRate total={totalBalance} interestRate={interestRate} color={lightColor} />
        </div>
        <div className="totals-panel__category-mobile">
            {chartLabel}
            <TotalRow label={labels.expectedTotalValueLabel}>
                <Total total={totalBalance} />
            </TotalRow>
            <TotalRow label={labels.percentageLabel}>
                <ProfitTag percentage={interestRate} color={lightColor} />
            </TotalRow>
            <TotalRow label={labels.amountLabel}>
                <TotalGained gained={totalGained} />
            </TotalRow>
        </div>
    </div>
);

const TotalGained = ({ gained }: { gained: number }) => (
    <p className="totals-panel__category__total-gained">{`+ ${formatNumberWithCurrency(gained, 'kr', 0)}`}</p>
);

const TotalRow = ({ children, label }: { children: React.ReactNode; label: string }) => (
    <div className="totals-panel__category-mobile__row">
        <span>{label}</span>
        <div>{children}</div>
    </div>
);

interface BalanceDetailsProps {
    total: number;
    interestRate: number;
    color?: string;
}

const TotalWithInterestRate = ({ total, interestRate, color }: BalanceDetailsProps) => (
    <div className="total-with-interest-rate">
        <Total total={total} />
        <ProfitTag percentage={interestRate} color={color} />
    </div>
);

const Total = ({ total }: { total: number }) => {
    const { totalsPanel } = useTranslation();

    return (
        <>
            <span className="total-with-interest-rate__total" aria-hidden="true">
                {formatNumber(total, 0)}
            </span>
            <ScreenReaderElement
                text={format(totalsPanel.profitTagA11yTotal, formatNumberWithCurrency(total, 'kr', 0))}
            />
        </>
    );
};
